import { CONNECTOR_EVENTS, Web3Auth } from '@web3auth/modal'
import { action, computed, observable, runInAction, when } from 'mobx'
import { AUTH_CONFIG, AUTH_CREATE_STORE_KEY } from '@/constants/auth.js'
import UserModel from '@/models/UserModel.js'

export default class AuthStore {
  rootStore = null

  #web3AuthClient = null

  @observable accessor isInitialized = false
  @observable accessor user = null
  @observable accessor signature = ''
  @observable accessor mustCreateStore = false

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerAfterConstructor(this.#_afterConstructor)
    this.rootStore.registerDeconstructor(this.#_deconstructor)
  }

  #_afterConstructor = async () => {
    this.#migrate()

    when(
      () =>
        this.router.isInitialized &&
        this.fingerprint.isInitialized &&
        this.fingerprint.hasAcceptedLegalDocs &&
        !this.fingerprint.isBot,
      () => this.#init()
    )
  }

  @action
  #_deconstructor = () => {
    this.isInitialized = false
    this.user.reset()
    this.user = null
    this.signature = ''

    this.#resetClient()
  }

  #init = async () => {
    if (!this.#canRestoreMfaSession) {
      runInAction(() => {
        this.isInitialized = true
      })

      console.log('[Auth] No session to restore')

      return
    }

    if (localStorage.getItem(AUTH_CREATE_STORE_KEY) === 'true') {
      localStorage.removeItem(AUTH_CREATE_STORE_KEY)
      runInAction(() => {
        this.mustCreateStore = true
      })
    }

    try {
      console.log('[Auth] Restoring session...')
      await this.login('', true)
    } catch (e) {
      console.error('[Auth] Failed to restore session', e)

      await this.logout().catch(e => {
        console.error('[Auth] Failed to logout after failed init', e)
      })
      this.router.reload()
    }
  }

  get analytics() {
    return this.rootStore.analyticsStore
  }

  get api() {
    return this.rootStore.apiStore
  }

  get fingerprint() {
    return this.rootStore.fingerprintStore
  }

  get router() {
    return this.rootStore.routerStore
  }

  get theme() {
    return this.rootStore.themeStore
  }

  get wallet() {
    return this.rootStore.walletStore
  }

  @computed
  get isLoggedIn() {
    return Boolean(this.user)
  }

  @computed
  get mustFinishRegistration() {
    return Boolean(this.user) && !this.user.handle && this.mustCreateStore
  }

  // not computed
  get #canRestoreMfaSession() {
    if (this.router.hash.startsWith('b64Params')) return true // redirect after mfa login

    const x = localStorage.getItem('auth_store')

    return Boolean(x) && Boolean(JSON.parse(x).sessionId)
  }

  login = async (redirectUrl = '', restoreSession = false) => {
    if (this.isLoggedIn) return

    localStorage.removeItem(AUTH_CREATE_STORE_KEY)

    this.#resetClient()
    this.#initClient()

    await this.#web3AuthClient.init(redirectUrl ? { redirectUrl } : {})

    if (!this.#web3AuthClient.connected && !restoreSession) await this.#web3AuthClient.connect()
  }

  loginAndCreateStore = async (isBtnClick = true) => {
    if (isBtnClick) this.analytics.clickOnCreateStore()

    localStorage.setItem(AUTH_CREATE_STORE_KEY, 'true')

    if (this.isLoggedIn && !this.user.hasStore)
      return this.router.navigate(this.router.routes.index, {
        ignoreBlocker: true,
        reloadDocument: true,
      })

    return this.login(this.router.buildUrl({ to: this.router.routes.index }))
  }

  logout = async () => {
    console.log('[Auth] Logging out...')

    return this.#web3AuthClient?.logout({ cleanup: true })
  }

  #initClient = () => {
    if (this.#web3AuthClient) return

    console.log('[Auth] Initializing client...')

    this.#web3AuthClient = new Web3Auth({
      ...AUTH_CONFIG,
      uiConfig: {
        ...AUTH_CONFIG.uiConfig,
        mode: this.theme.isDarkTheme ? 'dark' : 'light',
      },
    })

    this.#web3AuthClient.on(CONNECTOR_EVENTS.CONNECTED, this.#onConnected)
    this.#web3AuthClient.on(CONNECTOR_EVENTS.DISCONNECTED, this.#onDisconnected)
    this.#web3AuthClient.on(CONNECTOR_EVENTS.REHYDRATION_ERROR, this.#onRehydrationError)
  }

  #resetClient = () => {
    if (!this.#web3AuthClient) return

    console.log('[Auth] Resetting client...')

    this.#web3AuthClient.off(CONNECTOR_EVENTS.CONNECTED, this.#onConnected)
    this.#web3AuthClient.off(CONNECTOR_EVENTS.DISCONNECTED, this.#onDisconnected)
    this.#web3AuthClient.off(CONNECTOR_EVENTS.REHYDRATION_ERROR, this.#onRehydrationError)
    this.#web3AuthClient = null
  }

  #onConnected = async () => {
    console.log('[Auth] Connected')

    await this.wallet.init(this.#web3AuthClient.provider)

    // this creates a user address in backend if it's a new user and always sends uid that needs to be signed
    const sigResult = await this.#updateSignature()
    if (!sigResult) return

    const userResult = await this.#updateUser()
    if (!userResult) return

    await this.wallet.updateAll()

    if (!this.isInitialized)
      runInAction(() => {
        this.isInitialized = true
      })

    this.api
      .sendEvent('connectWeb3auth', { hash: this.user.email })
      .then(x => {
        if (Boolean(x?.data?.firstTime) === true) this.analytics.signUp()
      })
      .catch()
  }

  #onDisconnected = async () => {
    console.log('[Auth] Disconnected')
    this.wallet.reset()
    this.#resetClient()

    runInAction(() => {
      this.user?.reset()
      this.user = null
      this.signature = ''
    })

    await this.router.navigate(this.router.routes.index, {
      ignoreBlocker: true,
      reloadDocument: true,
    })
  }

  @action
  #onRehydrationError = () => {
    console.log('[Auth] Rehydration error, most likely session expired')
    this.isInitialized = true
    this.#resetClient()
  }

  #updateSignature = async () => {
    console.log('[Auth] Updating signature...')

    const userInfo = {
      ...(await this.#web3AuthClient.getUserInfo()),
      address: this.wallet.address,
      origin: window.origin,
      visitorId: this.fingerprint.visitorId,
    }

    const response = await this.api.updateSignature(userInfo)

    if (response?.data?.status !== 'OK') {
      console.error('[Auth] Failed to update signature')
      await this.#web3AuthClient.logout({ cleanup: true })

      return false
    }

    const signedMessage = await this.wallet.signMessage(response.data.uid)

    if (!signedMessage) {
      console.error('[Auth] Failed to sign message')
      await this.#web3AuthClient.logout({ cleanup: true })

      return false
    }

    runInAction(() => {
      this.signature = signedMessage
    })

    return true
  }

  #updateUser = async () => {
    console.log('[Auth] Retrieving user data...')
    const response = await this.api.getCurrentUserData()

    if (response?.data?.status !== 'OK') {
      console.error('[Auth] Failed to get user data')
      await this.#web3AuthClient.logout({ cleanup: true })

      return false
    }

    runInAction(() => {
      response.data.address = this.wallet.address
      this.user = new UserModel(this.rootStore, response.data, true)
    })

    await this.user.updateCategories()

    return true
  }

  #migrate = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('walletType')
    localStorage.removeItem('address')
    localStorage.removeItem('name')
    localStorage.removeItem('postOwnerNickname')
    localStorage.removeItem('agreeTCandPP')
    localStorage.removeItem('email')
    localStorage.removeItem('oldEmail')
    localStorage.removeItem('user')
    localStorage.removeItem('signature')
    localStorage.removeItem('hash')
    localStorage.removeItem('defaultRoomName')
    localStorage.removeItem('defaultPrice')
    localStorage.removeItem('defaultCPPT')
    localStorage.removeItem('openlogin_store')
    localStorage.removeItem('cover')
    localStorage.removeItem('handle')
    localStorage.removeItem('guestHandle')
    localStorage.removeItem('customProfileImage')
    localStorage.removeItem('defaultPPT')
    localStorage.removeItem('defaultPPH')
    localStorage.removeItem('start_siginin')
    localStorage.removeItem('start_siginin_guest')
    localStorage.removeItem('campaign')
    localStorage.removeItem('SFA')
    localStorage.removeItem('MFA')
    localStorage.removeItem('dMFA')
    localStorage.removeItem('gcConnected')
    localStorage.removeItem('idToken')
    localStorage.removeItem('walletType')
    localStorage.removeItem('video')
    localStorage.removeItem('moreData')
    localStorage.removeItem('user_return')
    localStorage.removeItem('enter')
    localStorage.removeItem('dashboard')
    localStorage.removeItem('isPromeetAdded')
    localStorage.removeItem('containerTop')
    localStorage.removeItem('createStore')
    localStorage.removeItem('wall')
  }
}
